import React, { useEffect, useState } from "react";
import useMedia from "use-media";
import { PaginationNext, PaginationPrev } from "../assets";

/**
 * A component that renders the pagination component.
 *
 * @param {Object} props
 * @param {number} props.currentPage - The current selected page
 * @param {(e: React.SetStateAction<number>)=>setCurrentPage} props.setCurrentPage - A function to set the state of the current select page
 * @param {number} props.totalPages - The total pages count
 * @returns {JSX.Element}
 */

export const Pagination = ({ currentPage, setCurrentPage, totalPages }) => {
  const maxMobilePages = 3;
  const isSmallScreen = useMedia("(max-width: 768px)");
  const [pageNumbers, setPageNumbers] = useState([]);

  useEffect(() => {
    const calculatePageNumbers = () => {
      const newPageNumbers = [];

      if (!isSmallScreen || totalPages <= maxMobilePages) {
        // Show all pages if not on a small screen or if total pages are less than maxMobilePages
        for (let i = 1; i <= totalPages; i++) {
          newPageNumbers.push(i);
        }
      } else {
        // Always show the first two pages
        newPageNumbers.push(1);
        newPageNumbers.push(2);

        // Calculate the range of pages to show around the current page
        const middlePages = Math.floor(maxMobilePages / 2);
        const startPage = Math.max(3, currentPage - middlePages);
        const endPage = Math.min(totalPages - 2, currentPage + middlePages);

        // Add ellipsis if there's a gap between the first two pages and the middle pages
        if (startPage > 3) {
          newPageNumbers.push("...");
        }

        // Add the middle pages
        for (let i = startPage; i <= endPage; i++) {
          newPageNumbers.push(i);
        }

        // Add ellipsis if there's a gap between the middle pages and the last two pages
        if (endPage < totalPages - 2) {
          newPageNumbers.push("...");
        }

        // Always show the last two pages
        newPageNumbers.push(totalPages - 1);
        newPageNumbers.push(totalPages);
      }

      setPageNumbers(newPageNumbers);
    };

    calculatePageNumbers();
  }, [currentPage, totalPages, isSmallScreen]);

  return (
    <div className="flex justify-center items-center text-text_white">
      <button
        className="bg-text_white text-background_steel_blue p-3 w-fit rounded-s-md"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      >
        <img
          src={PaginationPrev}
          className="w-3 h-3 rtl:rotate-180"
          alt="prev"
        />
      </button>
      {pageNumbers.map((page, index) => (
        <button
          disabled={currentPage === page || page === "..."}
          key={index} // Use index as key to avoid issues with duplicate ellipses
          onClick={() => page !== "..." && setCurrentPage(page)}
          className={`cursor-pointer w-9 h-9 ${
            currentPage === page
              ? "text-text_white bg-background_steel_blue"
              : "text-text_steel_blue bg-background_white"
          } border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white ${
            page === "..." ? "cursor-default" : ""
          }`}
        >
          {page}
        </button>
      ))}
      <button
        className="bg-text_white text-background_steel_blue p-3 w-fit rounded-e-md"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
      >
        <img
          src={PaginationNext}
          className="w-3 h-3 rtl:rotate-180"
          alt="next"
        />
      </button>
    </div>
  );
};
